import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, Checkbox, Menu, MenuItem, InputAdornment,
  FormControl, Select, InputLabel, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Check as EnabledIcon,
  Block as DisabledIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Business as CompanyIcon,
  Person as ContactIcon,
  Groups as BothIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import InternationalPhoneField from '../../components/common/InternationalPhoneField';
import CountrySelectField from '../../components/common/CountrySelectField';

const PROMOTE_TABS = [
  { id: 'all', label: 'All email', promotionStatus: '' },
  { id: 'contact', label: 'Promoted to Contact', promotionStatus: 'contact' },
  { id: 'company', label: 'Promoted to Company', promotionStatus: 'company' },
  { id: 'both', label: 'Promoted to Both', promotionStatus: 'both' }
];

const ExtractedEmails = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission('email_extractor_admin');

  // State
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');
  const [promoteTab, setPromoteTab] = useState(0);
  const [companyTagFilter, setCompanyTagFilter] = useState('');
  const [contactTagFilter, setContactTagFilter] = useState('');
  const [extractorTags, setExtractorTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [domains, setDomains] = useState([]);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Promote dialog
  const [promoteDialog, setPromoteDialog] = useState({ open: false, email: null });
  const [promoteForm, setPromoteForm] = useState({
    mode: 'contact',
    contact_type: 'candidate',
    first_name: '',
    last_name: '',
    phone: '',
    notes: '',
    company_name: '',
    website: '',
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    country: ''
  });
  const [promoteError, setPromoteError] = useState('');
  const [promoteSuccess, setPromoteSuccess] = useState('');
  const [viewDialog, setViewDialog] = useState({ open: false, email: null });
  const [editDialog, setEditDialog] = useState({ open: false, email: null });
  const [editForm, setEditForm] = useState({});

  const activePromotionStatus = PROMOTE_TABS[promoteTab]?.promotionStatus || '';

  // Fetch emails
  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(search && { search }),
        ...(domainFilter && { domain: domainFilter }),
        ...(sourceFilter && { source: sourceFilter }),
        ...(enabledFilter && { enabled: enabledFilter }),
        ...(activePromotionStatus && { promotionStatus: activePromotionStatus }),
        ...(companyTagFilter && { companyTag: companyTagFilter }),
        ...(contactTagFilter && { contactTag: contactTagFilter })
      });

      const response = await api.get(`/email-extractor/emails?${params}`);
      setEmails(response.data?.data || []);
      setTotalCount(response.data?.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, domainFilter, sourceFilter, enabledFilter, activePromotionStatus, companyTagFilter, contactTagFilter]);

  // Fetch domains for filter
  const fetchDomains = useCallback(async () => {
    try {
      const response = await api.get('/email-extractor/emails/domains');
      setDomains((response.data?.data || []).slice(0, 50)); // Top 50 domains
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await api.get('/email-extractor/tags');
      setExtractorTags(response.data?.data || []);
    } catch {
      setExtractorTags([]);
    }
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  useEffect(() => {
    fetchDomains();
    fetchTags();
  }, [fetchDomains, fetchTags]);

  // Handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(emails.map(e => e.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter(s => s !== id);
    }
    setSelected(newSelected);
  };

  const handleToggleStatus = async (id) => {
    try {
      await api.patch(`/email-extractor/emails/${id}/toggle`);
      fetchEmails();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const handleBulkToggle = async (enabled) => {
    try {
      setProcessing(true);
      await api.patch('/email-extractor/emails/bulk/toggle', {
        ids: selected,
        enabled
      });
      setSelected([]);
      fetchEmails();
      setActionMenuAnchor(null);
    } catch (error) {
      console.error('Failed to bulk toggle:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setProcessing(true);
      await api.post('/email-extractor/emails/bulk/delete', {
        ids: selected
      });
      setSelected([]);
      setDeleteDialog(false);
      fetchEmails();
    } catch (error) {
      console.error('Failed to bulk delete:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...(domainFilter && { domain: domainFilter }),
        ...(sourceFilter && { source: sourceFilter }),
        ...(enabledFilter && { enabled: enabledFilter })
      });

      const response = await api.get(`/email-extractor/emails/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `emails_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const sourceColors = {
    manual: 'success',
    csv: 'info',
    gmail_oauth: 'error',
    gmail_imap: 'warning',
    outlook: 'primary',
    yahoo: 'secondary',
    pst: 'default'
  };

  // Open promote dialog
  const openPromoteDialog = (email, mode = 'contact') => {
    if (email.promotion_status && email.promotion_status !== 'none') return;
    setPromoteForm({
      mode,
      contact_type: 'regular',
      first_name: email.first_name || '',
      last_name: email.last_name || '',
      phone: email.inferred_phone || '',
      notes: '',
      company_name: email.company_name || '',
      website: email.company_website || '',
      address_line1: email.company_address_line1 || '',
      city: email.company_city || '',
      state: email.company_state || '',
      zip_code: email.company_zip || '',
      country: email.company_country || ''
    });
    setPromoteError('');
    setPromoteSuccess('');
    setPromoteDialog({ open: true, email });
  };

  const openViewDialog = async (email) => {
    try {
      const r = await api.get(`/email-extractor/emails/${email.id}`);
      setViewDialog({ open: true, email: r.data?.data || email });
    } catch {
      setViewDialog({ open: true, email });
    }
  };

  const openEditDialog = (email) => {
    if (email.promotion_status && email.promotion_status !== 'none') {
      toast.info('Already promoted — editing is locked');
      return;
    }
    setEditForm({
      first_name: email.first_name || '',
      last_name: email.last_name || '',
      company_name: email.company_name || '',
      company_website: email.company_website || '',
      inferred_phone: email.inferred_phone || '',
      company_address_line1: email.company_address_line1 || '',
      company_city: email.company_city || '',
      company_state: email.company_state || '',
      company_zip: email.company_zip || '',
      company_country: email.company_country || '',
    });
    setEditDialog({ open: true, email });
  };

  const handleSaveEdit = async () => {
    try {
      setProcessing(true);
      await api.put(`/email-extractor/emails/${editDialog.email.id}`, editForm);
      toast.success('Updated');
      setEditDialog({ open: false, email: null });
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Update failed');
    } finally {
      setProcessing(false);
    }
  };

  // Handle promote
  const handlePromote = async () => {
    try {
      setProcessing(true);
      setPromoteError('');
      await api.post(`/email-extractor/emails/${promoteDialog.email.id}/promote`, promoteForm);
      setPromoteSuccess('Promotion completed successfully!');
      toast.success('Created company/contact record(s)');
      setPromoteDialog({ open: false, email: null });
      fetchEmails();
    } catch (error) {
      setPromoteError(error.response?.data?.message || 'Failed to promote');
    } finally {
      setProcessing(false);
    }
  };

  // Handle bulk promote
  const handleBulkPromote = async (mode, contactType = 'regular') => {
    if (!selected.length) {
      toast.info('Select one or more emails first');
      return;
    }
    try {
      setProcessing(true);
      const r = await api.post('/email-extractor/emails/bulk/promote', {
        email_ids: selected,
        mode,
        contact_type: contactType
      });
      toast.success(r.data?.message || `Promoted ${selected.length}`);
      setSelected([]);
      setActionMenuAnchor(null);
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to bulk promote');
    } finally {
      setProcessing(false);
    }
  };

  const isAlreadyPromoted = (email) =>
    Boolean(email?.promotion_status && email.promotion_status !== 'none');

  const promotionChip = (status) => {
    if (!status || status === 'none') return null;
    const color = status === 'both' ? 'success' : status === 'company' ? 'warning' : 'info';
    const label =
      status === 'both' ? 'Both' : status === 'company' ? 'Company' : 'Contact';
    return <Chip label={label} size="small" color={color} />;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/email-extractor')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Extracted Emails
          </Typography>
          <Chip label={`${(totalCount || 0).toLocaleString()} total`} size="small" />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isAdmin && (
            <>
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={<CompanyIcon />}
                disabled={!selected.length || processing}
                onClick={() => handleBulkPromote('company')}
              >
                Promote to Company
              </Button>
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<ContactIcon />}
                disabled={!selected.length || processing}
                onClick={() => handleBulkPromote('contact', 'regular')}
              >
                Promote to Contact
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<BothIcon />}
                disabled={!selected.length || processing}
                onClick={() => handleBulkPromote('both', 'regular')}
              >
                Promote Both
              </Button>
            </>
          )}
          {hasPermission('email_extractor_csv') && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={promoteTab}
          onChange={(_, v) => {
            setPromoteTab(v);
            setPage(0);
            setSelected([]);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {PROMOTE_TABS.map((t) => (
            <Tab key={t.id} label={t.label} />
          ))}
        </Tabs>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search emails..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Domain</InputLabel>
              <Select
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                label="Domain"
              >
                <MenuItem value="">All</MenuItem>
                {domains.map(d => (
                  <MenuItem key={d.domain} value={d.domain}>
                    {d.domain} ({d.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                label="Source"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="gmail_oauth">Gmail OAuth</MenuItem>
                <MenuItem value="gmail_imap">Gmail IMAP</MenuItem>
                <MenuItem value="outlook">Outlook</MenuItem>
                <MenuItem value="yahoo">Yahoo</MenuItem>
                <MenuItem value="pst">PST</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Enabled</MenuItem>
                <MenuItem value="false">Disabled</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Company tag</InputLabel>
              <Select
                value={companyTagFilter}
                onChange={(e) => { setCompanyTagFilter(e.target.value); setPage(0); }}
                label="Company tag"
              >
                <MenuItem value="">All</MenuItem>
                {extractorTags.filter((t) => t.category === 'company').map((t) => (
                  <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Contact tag</InputLabel>
              <Select
                value={contactTagFilter}
                onChange={(e) => { setContactTagFilter(e.target.value); setPage(0); }}
                label="Contact tag"
              >
                <MenuItem value="">All</MenuItem>
                {extractorTags.filter((t) => t.category === 'contact').map((t) => (
                  <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="text"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearch('');
                setDomainFilter('');
                setSourceFilter('');
                setEnabledFilter('');
                setCompanyTagFilter('');
                setContactTagFilter('');
              }}
            >
              Clear
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selected.length > 0 && isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography>{selected.length} email(s) selected</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={(e) => setActionMenuAnchor(e.currentTarget)}>
                Actions
              </Button>
              <Button size="small" color="error" onClick={() => setDeleteDialog(true)}>
                Delete
              </Button>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkToggle(true)}>Enable Selected</MenuItem>
        <MenuItem onClick={() => handleBulkToggle(false)}>Disable Selected</MenuItem>
        <MenuItem divider />
        <MenuItem onClick={() => handleBulkPromote('contact', 'candidate')}>
          Promote Contact Only (Candidates)
        </MenuItem>
        <MenuItem onClick={() => handleBulkPromote('contact', 'regular')}>
          Promote Contact Only (Regular)
        </MenuItem>
        <MenuItem onClick={() => handleBulkPromote('company')}>Promote Company Only</MenuItem>
        <MenuItem onClick={() => handleBulkPromote('both', 'regular')}>Promote Both</MenuItem>
      </Menu>

      {/* Emails Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {isAdmin && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={emails.length > 0 && selected.length === emails.length}
                      indeterminate={selected.length > 0 && selected.length < emails.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell>Batch ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Company tag</TableCell>
                <TableCell>Contact tag</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Promotion</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Date</TableCell>
                {isAdmin && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 14 : 12} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : emails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 14 : 12} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No emails found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                emails.map((email) => (
                  <TableRow key={email.id} hover>
                    {isAdmin && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(email.id)}
                          onChange={() => handleSelect(email.id)}
                          disabled={isAlreadyPromoted(email)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Tooltip title={email.batch_id || ''}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {email.batch?.reference_number ||
                            (email.batch_id ? String(email.batch_id).slice(0, 8) : '—')}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {email.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {email.first_name || email.last_name
                        ? `${email.first_name || ''} ${email.last_name || ''}`
                        : '-'}
                    </TableCell>
                    <TableCell>{email.company_name || '—'}</TableCell>
                    <TableCell>
                      {email.company_tag_code ? (
                        <Chip size="small" label={email.company_tag_code} variant="outlined" />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {email.contact_tag_code ? (
                        <Chip size="small" label={email.contact_tag_code} variant="outlined" />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{email.domain}</TableCell>
                    <TableCell>
                      <Chip
                        label={email.source}
                        size="small"
                        color={sourceColors[email.source] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {promotionChip(email.promotion_status) || (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={email.enabled ? 'Enabled' : 'Disabled'}>
                        <IconButton
                          size="small"
                          color={email.enabled ? 'success' : 'error'}
                          onClick={() => isAdmin && handleToggleStatus(email.id)}
                          disabled={!isAdmin}
                        >
                          {email.enabled ? <EnabledIcon fontSize="small" /> : <DisabledIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {email.uploader
                        ? `${email.uploader.first_name} ${email.uploader.last_name}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(email.createdAt).toLocaleDateString()}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => openViewDialog(email)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isAlreadyPromoted(email) ? 'Locked after promote' : 'Edit'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(email)}
                              disabled={isAlreadyPromoted(email)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelected([email.id]);
                              setDeleteDialog(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Emails</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selected.length} email(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBulkDelete}
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promote Dialog */}
      <Dialog 
        open={promoteDialog.open} 
        onClose={() => setPromoteDialog({ open: false, email: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Promote Extracted Email</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {promoteError && (
              <Alert severity="error" onClose={() => setPromoteError('')}>
                {promoteError}
              </Alert>
            )}
            
            <Alert severity="info">
              Promote <strong>{promoteDialog.email?.email}</strong>. Once promoted, this record cannot be promoted again.
            </Alert>

            <FormControl fullWidth size="small">
              <InputLabel>Promote mode</InputLabel>
              <Select
                value={promoteForm.mode}
                label="Promote mode"
                onChange={(e) => setPromoteForm({ ...promoteForm, mode: e.target.value })}
              >
                <MenuItem value="contact">Promote Contact Only</MenuItem>
                <MenuItem value="company">Promote Company Only</MenuItem>
                <MenuItem value="both">Promote Both</MenuItem>
              </Select>
            </FormControl>

            {(promoteForm.mode === 'contact' || promoteForm.mode === 'both') && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Contact Type</InputLabel>
                  <Select
                    value={promoteForm.contact_type}
                    label="Contact Type"
                    onChange={(e) => setPromoteForm({ ...promoteForm, contact_type: e.target.value })}
                  >
                    <MenuItem value="regular">Contact</MenuItem>
                    <MenuItem value="candidate">Candidate (Job Seeker)</MenuItem>
                    <MenuItem value="contractor_independent">Contractor (Independent)</MenuItem>
                    <MenuItem value="contractor_c2c">Contractor (C2C)</MenuItem>
                    <MenuItem value="employee">Employee</MenuItem>
                    <MenuItem value="trainer">Trainer / Instructor / Teacher</MenuItem>
                    <MenuItem value="trainee">Trainee</MenuItem>
                    <MenuItem value="company">Company</MenuItem>
                    <MenuItem value="guest_signedup">Guest</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  label="First Name"
                  size="small"
                  fullWidth
                  value={promoteForm.first_name}
                  onChange={(e) => setPromoteForm({ ...promoteForm, first_name: e.target.value })}
                />
                
                <TextField
                  label="Last Name"
                  size="small"
                  fullWidth
                  value={promoteForm.last_name}
                  onChange={(e) => setPromoteForm({ ...promoteForm, last_name: e.target.value })}
                />
              </>
            )}

            {(promoteForm.mode === 'company' || promoteForm.mode === 'both') && (
              <>
                <TextField
                  label="Company Name"
                  size="small"
                  fullWidth
                  required
                  value={promoteForm.company_name}
                  onChange={(e) => setPromoteForm({ ...promoteForm, company_name: e.target.value })}
                  helperText="Creates an Account (company) record"
                />
                <TextField
                  label="Website"
                  size="small"
                  fullWidth
                  value={promoteForm.website}
                  onChange={(e) => setPromoteForm({ ...promoteForm, website: e.target.value })}
                />
              </>
            )}
            
            <InternationalPhoneField
              label="Phone"
              name="phone"
              compact
              value={promoteForm.phone}
              onChange={(e) => setPromoteForm((prev) => ({ ...prev, phone: e.target.value }))}
            />
            
            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={promoteForm.notes}
              onChange={(e) => setPromoteForm({ ...promoteForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialog({ open: false, email: null })}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handlePromote}
            disabled={
              processing ||
              ((promoteForm.mode === 'company' || promoteForm.mode === 'both') &&
                !String(promoteForm.company_name || '').trim())
            }
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            {processing
              ? 'Promoting...'
              : promoteForm.mode === 'company'
                ? 'Promote Company Only'
                : promoteForm.mode === 'both'
                  ? 'Promote Both'
                  : 'Promote Contact Only'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, email: null })} maxWidth="sm" fullWidth>
        <DialogTitle>View extracted email</DialogTitle>
        <DialogContent dividers>
          {viewDialog.email && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
              <Typography variant="body2"><strong>Email:</strong> {viewDialog.email.email}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {[viewDialog.email.first_name, viewDialog.email.last_name].filter(Boolean).join(' ') || '—'}</Typography>
              <Typography variant="body2"><strong>Title:</strong> {viewDialog.email.contact_title || '—'}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> {viewDialog.email.inferred_phone || '—'}</Typography>
              <Typography variant="body2"><strong>Mobile:</strong> {viewDialog.email.inferred_mobile || '—'}</Typography>
              <Typography variant="body2">
                <strong>LinkedIn:</strong>{' '}
                {viewDialog.email.linkedin_url ? (
                  <a href={viewDialog.email.linkedin_url} target="_blank" rel="noreferrer">{viewDialog.email.linkedin_url}</a>
                ) : '—'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Company</Typography>
              <Typography variant="body2"><strong>Company:</strong> {viewDialog.email.company_name || '—'}</Typography>
              <Typography variant="body2"><strong>Website:</strong> {viewDialog.email.company_website || '—'}</Typography>
              <Typography variant="body2"><strong>Domain:</strong> {viewDialog.email.domain || '—'}</Typography>
              <Typography variant="body2"><strong>Address:</strong> {[viewDialog.email.company_address_line1, viewDialog.email.company_address_line2, viewDialog.email.company_city, viewDialog.email.company_state, viewDialog.email.company_zip, viewDialog.email.company_country].filter(Boolean).join(', ') || '—'}</Typography>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Classification</Typography>
              <Typography variant="body2"><strong>Company tag:</strong> {viewDialog.email.company_tag_code || '—'}</Typography>
              <Typography variant="body2"><strong>Contact tag:</strong> {viewDialog.email.contact_tag_code || '—'}</Typography>
              <Typography variant="body2"><strong>Promotion:</strong> {viewDialog.email.promotion_status || 'none'}</Typography>
              {viewDialog.email.promotedAccount && (
                <Typography variant="body2"><strong>Company code:</strong> {viewDialog.email.promotedAccount.account_code} — {viewDialog.email.promotedAccount.company_name}</Typography>
              )}
              {viewDialog.email.promotedContact && (
                <Typography variant="body2"><strong>Contact code:</strong> {viewDialog.email.promotedContact.contact_code}</Typography>
              )}

              {Array.isArray(viewDialog.email.parse_hints) && viewDialog.email.parse_hints.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>How these values were inferred</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {viewDialog.email.parse_hints.map((h, i) => (
                      <Typography component="li" variant="caption" key={i} color="text.secondary">{h}</Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, email: null })}>Close</Button>
          {viewDialog.email && !isAlreadyPromoted(viewDialog.email) && (
            <Button onClick={() => { setViewDialog({ open: false, email: null }); openPromoteDialog(viewDialog.email, 'both'); }}>
              Promote…
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, email: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit extracted email</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="First name" size="small" value={editForm.first_name || ''} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
            <TextField label="Last name" size="small" value={editForm.last_name || ''} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
            <TextField label="Company name" size="small" value={editForm.company_name || ''} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} />
            <TextField label="Website" size="small" value={editForm.company_website || ''} onChange={(e) => setEditForm({ ...editForm, company_website: e.target.value })} />
            <InternationalPhoneField label="Phone" name="inferred_phone" compact value={editForm.inferred_phone || ''} onChange={(e) => setEditForm({ ...editForm, inferred_phone: e.target.value })} />
            <TextField label="Address line 1" size="small" value={editForm.company_address_line1 || ''} onChange={(e) => setEditForm({ ...editForm, company_address_line1: e.target.value })} />
            <TextField label="City" size="small" value={editForm.company_city || ''} onChange={(e) => setEditForm({ ...editForm, company_city: e.target.value })} />
            <TextField label="State" size="small" value={editForm.company_state || ''} onChange={(e) => setEditForm({ ...editForm, company_state: e.target.value })} />
            <TextField label="ZIP" size="small" value={editForm.company_zip || ''} onChange={(e) => setEditForm({ ...editForm, company_zip: e.target.value })} />
            <CountrySelectField
              label="Country"
              size="small"
              margin="none"
              value={editForm.company_country || ''}
              onChange={(v) => setEditForm({ ...editForm, company_country: v })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, email: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={processing}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExtractedEmails;
